import { Head, Link, router, useForm } from '@inertiajs/react'
import type { FormEvent } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { spotifyEmbedUrl, youtubeEmbedUrl } from '@/lib/embeds'

type Status = 'queued' | 'learning' | 'known'
type RecordingKind = 'file' | 'spotify' | 'youtube'

interface Tune {
  id: number
  name: string
  status: Status
  notes: string | null
  tune_type: string | null
  musical_key: string | null
  composer: string | null
}

interface Recording {
  id: number
  kind: string
  url: string | null
  title: string | null
  primary: boolean
  audio_file: string | null
}

interface Props {
  tune: Tune
  recordings: Recording[]
}

const STATUSES: Status[] = ['queued', 'learning', 'known']
const RECORDING_KINDS: RecordingKind[] = ['file', 'spotify', 'youtube']

export default function Show({ tune, recordings }: Props) {
  const form = useForm({ tune: { notes: tune.notes ?? '' } })
  const recForm = useForm<{
    kind: RecordingKind
    title: string
    url: string
    audio_file: File | null
  }>({ kind: 'file', title: '', url: '', audio_file: null })

  function saveNotes(e: FormEvent) {
    e.preventDefault()
    form.patch(`/tunes/${tune.id}`, { preserveScroll: true })
  }

  function addRecording(e: FormEvent) {
    e.preventDefault()
    recForm.post(`/tunes/${tune.id}/recordings`, {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => recForm.reset(),
    })
  }

  function makePrimary(recordingId: number) {
    router.patch(
      `/tunes/${tune.id}/recordings/${recordingId}/set_primary`,
      {},
      { preserveScroll: true },
    )
  }

  function deleteRecording(recordingId: number) {
    if (confirm('Delete this recording?')) {
      router.delete(`/tunes/${tune.id}/recordings/${recordingId}`, { preserveScroll: true })
    }
  }

  function changeStatus(status: Status) {
    router.patch(`/tunes/${tune.id}`, { tune: { status } }, { preserveScroll: true })
  }

  function destroy() {
    if (confirm(`Delete "${tune.name}"?`)) {
      router.delete(`/tunes/${tune.id}`)
    }
  }

  const meta = [tune.tune_type, tune.musical_key, tune.composer].filter(Boolean).join(' · ')

  return (
    <>
      <Head title={tune.name} />

      <div className="mb-6 flex items-center justify-between">
        <div>
          <Link href="/tunes" className="text-sm text-muted-foreground hover:underline">
            ← Tunes
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">{tune.name}</h1>
          {meta && <p className="text-sm text-muted-foreground">{meta}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link href={`/tunes/${tune.id}/edit`}>Edit</Link>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Badge className="cursor-pointer capitalize">{tune.status}</Badge>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {STATUSES.map((s) => (
                <DropdownMenuItem key={s} className="capitalize" onSelect={() => changeStatus(s)}>
                  {s}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={saveNotes} className="space-y-3">
              <Label htmlFor="notes" className="sr-only">
                Notes
              </Label>
              <Textarea
                id="notes"
                rows={5}
                placeholder="Settings, ornaments, where you heard it…"
                value={form.data.tune.notes}
                onChange={(e) => form.setData('tune', { notes: e.target.value })}
              />
              <Button type="submit" size="sm" disabled={form.processing}>
                Save notes
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recordings</CardTitle>
          </CardHeader>
          <CardContent>
            {recordings.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recordings yet.</p>
            ) : (
              <ul className="space-y-3">
                {recordings.map((r) => (
                  <li key={r.id} className="rounded-md border p-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Badge variant="outline">{r.kind}</Badge>
                      {r.title && <span className="font-medium">{r.title}</span>}
                      {r.primary && <Badge variant="secondary">primary</Badge>}
                      <div className="ml-auto flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={r.primary}
                          onClick={() => makePrimary(r.id)}
                        >
                          {r.primary ? 'Primary' : 'Make primary'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteRecording(r.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                    {r.audio_file ? (
                      <audio controls src={r.audio_file} className="mt-2 w-full">
                        <track kind="captions" />
                      </audio>
                    ) : r.kind === 'spotify' && spotifyEmbedUrl(r.url) ? (
                      <iframe
                        title="Spotify player"
                        src={spotifyEmbedUrl(r.url) ?? undefined}
                        loading="lazy"
                        allow="encrypted-media"
                        className="mt-2 w-full rounded-xl border-0"
                        height={152}
                      />
                    ) : r.kind === 'youtube' && youtubeEmbedUrl(r.url) ? (
                      <iframe
                        title="YouTube player"
                        src={youtubeEmbedUrl(r.url) ?? undefined}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="mt-2 aspect-video w-full rounded-xl border-0"
                      />
                    ) : (
                      r.url && (
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 inline-block text-sm text-primary hover:underline"
                        >
                          {r.url}
                        </a>
                      )
                    )}
                  </li>
                ))}
              </ul>
            )}

            <form onSubmit={addRecording} className="mt-4 space-y-3 border-t pt-4">
              <p className="text-sm font-medium">Add recording</p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label htmlFor="rec-kind">Kind</Label>
                  <Select
                    value={recForm.data.kind}
                    onValueChange={(v) => recForm.setData('kind', v as RecordingKind)}
                  >
                    <SelectTrigger id="rec-kind" className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RECORDING_KINDS.map((k) => (
                        <SelectItem key={k} value={k}>
                          {k}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {recForm.data.kind === 'file' ? (
                  <div className="space-y-1">
                    <Label htmlFor="rec-file">Audio file</Label>
                    <Input
                      id="rec-file"
                      type="file"
                      accept="audio/*,.m4a,.mp3,.wav,.aac,.aiff,.caf,.m4b,.ogg,.opus,.flac"
                      onChange={(e) => recForm.setData('audio_file', e.target.files?.[0] ?? null)}
                    />
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Label htmlFor="rec-url">URL</Label>
                    <Input
                      id="rec-url"
                      type="url"
                      placeholder="https://…"
                      value={recForm.data.url}
                      onChange={(e) => recForm.setData('url', e.target.value)}
                    />
                  </div>
                )}

                <div className="space-y-1">
                  <Label htmlFor="rec-title">Title (optional)</Label>
                  <Input
                    id="rec-title"
                    value={recForm.data.title}
                    onChange={(e) => recForm.setData('title', e.target.value)}
                  />
                </div>

                <Button type="submit" size="sm" disabled={recForm.processing}>
                  Add
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Button variant="destructive" onClick={destroy}>
          Delete tune
        </Button>
      </div>
    </>
  )
}
